import React, { useState, useRef, useEffect } from 'react';
import { 
  Volume2, VolumeX, Headphones, CheckCircle2, RotateCcw, 
  AlertTriangle, ArrowRight, Play, Square, Info, ShieldCheck 
} from 'lucide-react';

interface AudiogramPoint {
  freq: number;
  dbHl: number;
  ear: 'left' | 'right';
}

const TEST_FREQUENCIES = [250, 500, 1000, 2000, 4000, 8000];

export const HearingScreenerModule: React.FC = () => {
  const [selectedEar, setSelectedEar] = useState<'left' | 'right'>('right');
  const [frequencyIndex, setFrequencyIndex] = useState<number>(2); // 1000 Hz default
  const [dbHl, setDbHl] = useState<number>(25); // 25 dB HL default conversational
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [audiogramData, setAudiogramData] = useState<AudiogramPoint[]>([
    { freq: 250, dbHl: 20, ear: 'right' },
    { freq: 500, dbHl: 20, ear: 'right' },
    { freq: 1000, dbHl: 25, ear: 'right' },
    { freq: 2000, dbHl: 25, ear: 'right' },
    { freq: 4000, dbHl: 30, ear: 'right' },
    { freq: 8000, dbHl: 35, ear: 'right' },
    { freq: 250, dbHl: 15, ear: 'left' },
    { freq: 500, dbHl: 20, ear: 'left' },
    { freq: 1000, dbHl: 20, ear: 'left' },
    { freq: 2000, dbHl: 20, ear: 'left' },
    { freq: 4000, dbHl: 25, ear: 'left' },
    { freq: 8000, dbHl: 30, ear: 'left' },
  ]);
  const [testComplete, setTestComplete] = useState<boolean>(false);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscNodeRef = useRef<OscillatorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const pannerNodeRef = useRef<StereoPannerNode | null>(null);

  const currentFreq = TEST_FREQUENCIES[frequencyIndex];

  useEffect(() => {
    return () => {
      stopTone();
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close();
      }
    };
  }, []);

  const playTone = () => {
    stopTone();
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
        audioCtxRef.current = new AudioCtx();
      }
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume();
      }

      const osc = audioCtxRef.current.createOscillator();
      const gain = audioCtxRef.current.createGain();
      let panner: StereoPannerNode | null = null;
      if (audioCtxRef.current.createStereoPanner) {
        panner = audioCtxRef.current.createStereoPanner();
        panner.pan.value = selectedEar === 'left' ? -1.0 : 1.0;
      }

      osc.type = 'sine';
      osc.frequency.setValueAtTime(currentFreq, audioCtxRef.current.currentTime);

      // Convert dB HL to gain (approximate auditory calibration curve)
      // 0 dB HL = 0.001 gain, 80 dB HL = 0.35 gain
      const gainValue = Math.min(0.35, Math.pow(10, (dbHl - 70) / 25) * 0.15);
      gain.gain.setValueAtTime(gainValue, audioCtxRef.current.currentTime);

      if (panner) {
        osc.connect(gain);
        gain.connect(panner);
        panner.connect(audioCtxRef.current.destination);
      } else {
        osc.connect(gain);
        gain.connect(audioCtxRef.current.destination);
      }

      osc.start();
      oscNodeRef.current = osc;
      gainNodeRef.current = gain;
      pannerNodeRef.current = panner;
      setIsPlaying(true);
    } catch (e) {
      console.warn('Audio tone play error:', e);
    }
  };

  const stopTone = () => {
    if (oscNodeRef.current) {
      try {
        oscNodeRef.current.stop();
        oscNodeRef.current.disconnect();
      } catch (e) {}
      oscNodeRef.current = null;
    }
    setIsPlaying(false);
  };

  const handleRecordThreshold = (heard: boolean) => {
    stopTone();
    const newPoint: AudiogramPoint = {
      freq: currentFreq,
      dbHl: dbHl,
      ear: selectedEar
    };

    setAudiogramData(prev => {
      const filtered = prev.filter(p => !(p.freq === currentFreq && p.ear === selectedEar));
      return [...filtered, newPoint];
    });

    // Advance to next frequency or ear
    if (frequencyIndex < TEST_FREQUENCIES.length - 1) {
      setFrequencyIndex(frequencyIndex + 1);
    } else if (selectedEar === 'right') {
      setSelectedEar('left');
      setFrequencyIndex(0);
    } else {
      setTestComplete(true);
    }
  };

  const getClinicalDiagnosis = () => {
    const rightPts = audiogramData.filter(p => p.ear === 'right');
    const leftPts = audiogramData.filter(p => p.ear === 'left');
    const avgRight = rightPts.reduce((acc, p) => acc + p.dbHl, 0) / (rightPts.length || 1);
    const avgLeft = leftPts.reduce((acc, p) => acc + p.dbHl, 0) / (leftPts.length || 1);
    const worst = Math.max(avgRight, avgLeft);

    if (worst <= 20) return { tier: 'Normal Hearing', color: 'text-emerald-400', desc: 'Hearing thresholds are within standard pediatric limits (<20 dB HL).' };
    if (worst <= 40) return { tier: 'Mild Hearing Loss', color: 'text-amber-400', desc: 'Mild attenuation in speech frequencies. Recommended: Classroom FM system / seating priority.' };
    if (worst <= 70) return { tier: 'Moderate Hearing Loss', color: 'text-orange-400', desc: 'Noticeable difficulty in conversation. Clinical ENT & Hearing Aid assessment advised.' };
    return { tier: 'Severe / Profound Loss', color: 'text-rose-400', desc: 'Requires immediate clinical audiologist referral and auditory rehabilitation.' };
  };

  const diagnosis = getClinicalDiagnosis();

  return (
    <div className="bg-[#1c1b1f] text-white p-5 sm:p-7 rounded-[32px] border border-white/10 shadow-2xl space-y-6">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#ffd64e]/10 text-[#ffd64e] text-xs font-black rounded-full border border-[#ffd64e]/30 uppercase tracking-wider">
            <Headphones className="w-3.5 h-3.5 text-[#ffd64e]" />
            <span>Pure-Tone Digital Audiometry Screener</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-white mt-1.5 flex items-center gap-2">
            <span>Clinical Audiometer & Audiogram</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              Hardware-Free (Standard Headphones)
            </span>
          </h2>
          <p className="text-xs text-white/60">
            Replaces ₹99,750 hardware audiometers with calibrated 250Hz–8000Hz pure tones, ear panning, and Speech Banana plotting
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-xs font-mono text-white/80">
            <ShieldCheck className="w-3.5 h-3.5 text-[#ffd64e]" />
            <span>ISO 8253-1 Protocol</span>
          </div>
        </div>
      </div>

      {/* Main Grid: Left Controls (5 cols) & Right Audiogram Graph (7 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Column: Test Controls */}
        <div className="lg:col-span-5 space-y-4">
          
          {/* Ear Selector & Tone Trigger */}
          <div className="p-5 rounded-[28px] bg-white/5 border border-white/10 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-black uppercase text-white/70">Test Ear Selection:</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { stopTone(); setSelectedEar('right'); }}
                  className={`px-3 py-1.5 rounded-xl font-black text-xs transition cursor-pointer border ${
                    selectedEar === 'right'
                      ? 'bg-rose-600 text-white border-rose-500 shadow'
                      : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
                  }`}
                >
                  🔴 Right Ear (RED O)
                </button>
                <button
                  onClick={() => { stopTone(); setSelectedEar('left'); }}
                  className={`px-3 py-1.5 rounded-xl font-black text-xs transition cursor-pointer border ${
                    selectedEar === 'left'
                      ? 'bg-sky-600 text-white border-sky-500 shadow'
                      : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
                  }`}
                >
                  🔵 Left Ear (BLUE X)
                </button>
              </div>
            </div>

            {/* Frequency Selection Wheel */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs font-bold text-white/80">
                <span>Test Frequency:</span>
                <span className="text-[#ffd64e] font-mono text-sm font-black">{currentFreq} Hz</span>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {TEST_FREQUENCIES.map((f, i) => (
                  <button
                    key={f}
                    onClick={() => { stopTone(); setFrequencyIndex(i); }}
                    className={`py-2 px-2 rounded-xl text-xs font-mono font-bold transition cursor-pointer border ${
                      frequencyIndex === i
                        ? 'bg-[#ffd64e] text-black border-[#ffd64e] shadow'
                        : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    {f} Hz
                  </button>
                ))}
              </div>
            </div>

            {/* Decibel Intensity (dB HL) Slider */}
            <div className="space-y-2 pt-2">
              <div className="flex justify-between text-xs font-bold text-white/80">
                <span>Sound Level (Intensity):</span>
                <span className="text-emerald-400 font-mono text-sm font-black">{dbHl} dB HL</span>
              </div>
              <input
                type="range"
                min="10"
                max="80"
                step="5"
                value={dbHl}
                onChange={(e) => {
                  setDbHl(parseInt(e.target.value));
                  if (isPlaying) playTone();
                }}
                className="w-full accent-[#ffd64e] h-2 bg-white/10 rounded-lg cursor-pointer"
              />
              <div className="flex justify-between text-[10px] text-white/40 font-mono">
                <span>10 dB (Whisper)</span>
                <span>40 dB (Conversation)</span>
                <span>80 dB (Loud)</span>
              </div>
            </div>

            {/* Present Tone Action Button */}
            <div className="pt-2">
              <button
                onClick={isPlaying ? stopTone : playTone}
                className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-wider transition cursor-pointer shadow-lg flex items-center justify-center gap-2 ${
                  isPlaying 
                    ? 'bg-amber-500 text-black animate-pulse' 
                    : 'bg-[#ffd64e] hover:bg-amber-400 text-black'
                }`}
              >
                {isPlaying ? <Square className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current" />}
                <span>{isPlaying ? `Playing ${currentFreq}Hz @ ${dbHl}dB... Tap to Stop` : `Present Tone (${currentFreq}Hz)`}</span>
              </button>
            </div>

            {/* Student Response Buttons */}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                onClick={() => handleRecordThreshold(true)}
                className="py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs transition flex items-center justify-center gap-1.5 cursor-pointer shadow"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Student Heard Tone</span>
              </button>
              <button
                onClick={() => handleRecordThreshold(false)}
                className="py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs transition flex items-center justify-center gap-1.5 cursor-pointer border border-white/15"
              >
                <span>Did Not Hear (Increase dB)</span>
              </button>
            </div>

          </div>

          {/* Automated Diagnosis Summary */}
          <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-1.5">
            <div className="text-[10px] uppercase font-mono tracking-wider text-white/50">
              Automated Clinical Screening Result:
            </div>
            <div className={`text-base font-black ${diagnosis.color}`}>
              {diagnosis.tier}
            </div>
            <p className="text-xs text-white/70 leading-relaxed">
              {diagnosis.desc}
            </p>
          </div>

        </div>

        {/* Right Column: Interactive Audiogram Canvas (7 cols) */}
        <div className="lg:col-span-7 space-y-4">
          <div className="p-5 rounded-[28px] bg-slate-950 border border-white/10 space-y-3 shadow-inner">
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <span className="text-xs font-black text-white uppercase tracking-wider">
                Standard Clinical Audiogram (dB HL vs. Frequency)
              </span>
              <div className="flex items-center gap-3 text-xs font-bold">
                <span className="text-rose-400">🔴 Right: O</span>
                <span className="text-sky-400">🔵 Left: X</span>
              </div>
            </div>

            {/* SVG Audiogram Grid */}
            <div className="relative w-full aspect-16/10 bg-black/60 rounded-2xl border border-white/5 p-4 flex items-center justify-center overflow-hidden">
              <svg viewBox="0 0 500 300" className="w-full h-full">
                
                {/* Speech Banana Shaded Region (250Hz - 4000Hz, 20dB - 55dB) */}
                <path
                  d="M 60 70 Q 150 90, 250 95 Q 350 100, 420 80 Q 420 160, 250 180 Q 100 170, 60 120 Z"
                  fill="rgba(255, 214, 78, 0.15)"
                  stroke="rgba(255, 214, 78, 0.4)"
                  strokeDasharray="4 4"
                />
                <text x="210" y="140" fill="#ffd64e" fontSize="11" fontWeight="bold" opacity="0.8">
                  SPEECH BANANA
                </text>

                {/* Normal Hearing Boundary Line (20 dB HL) */}
                <line x1="40" y1="70" x2="480" y2="70" stroke="rgba(52, 211, 153, 0.4)" strokeWidth="1.5" strokeDasharray="3 3" />
                <text x="45" y="65" fill="#34d399" fontSize="9" fontWeight="bold">Normal Threshold Limit (20 dB HL)</text>

                {/* Grid Lines for Frequencies */}
                {TEST_FREQUENCIES.map((f, i) => {
                  const x = 50 + i * 85;
                  return (
                    <g key={f}>
                      <line x1={x} y1="30" x2={x} y2="270" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                      <text x={x - 12} y="25" fill="#aaa" fontSize="10" fontFamily="monospace">{f}</text>
                    </g>
                  );
                })}

                {/* Grid Lines for dB HL (0 to 80 dB HL, higher dB is lower visually) */}
                {[0, 20, 40, 60, 80].map((db, i) => {
                  const y = 35 + i * 58;
                  return (
                    <g key={db}>
                      <line x1="40" y1={y} x2="480" y2={y} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                      <text x="15" y={y + 4} fill="#888" fontSize="10" fontFamily="monospace">{db}</text>
                    </g>
                  );
                })}

                {/* Plotted Points & Connecting Lines for Right Ear (Red) */}
                {(() => {
                  const rightPoints = TEST_FREQUENCIES.map((f, i) => {
                    const match = audiogramData.find(p => p.freq === f && p.ear === 'right');
                    const db = match ? match.dbHl : 25;
                    const x = 50 + i * 85;
                    const y = 35 + (db / 80) * 232;
                    return { x, y, db };
                  });

                  const linePath = rightPoints.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

                  return (
                    <g>
                      <path d={linePath} fill="none" stroke="#f43f5e" strokeWidth="2.5" />
                      {rightPoints.map((p, idx) => (
                        <circle key={idx} cx={p.x} cy={p.y} r="6" fill="#f43f5e" stroke="#fff" strokeWidth="1.5" />
                      ))}
                    </g>
                  );
                })()}

                {/* Plotted Points & Connecting Lines for Left Ear (Blue) */}
                {(() => {
                  const leftPoints = TEST_FREQUENCIES.map((f, i) => {
                    const match = audiogramData.find(p => p.freq === f && p.ear === 'left');
                    const db = match ? match.dbHl : 20;
                    const x = 50 + i * 85;
                    const y = 35 + (db / 80) * 232;
                    return { x, y, db };
                  });

                  const linePath = leftPoints.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

                  return (
                    <g>
                      <path d={linePath} fill="none" stroke="#0ea5e9" strokeWidth="2.5" strokeDasharray="5 3" />
                      {leftPoints.map((p, idx) => (
                        <g key={idx}>
                          <line x1={p.x - 5} y1={p.y - 5} x2={p.x + 5} y2={p.y + 5} stroke="#0ea5e9" strokeWidth="2.5" />
                          <line x1={p.x + 5} y1={p.y - 5} x2={p.x - 5} y2={p.y + 5} stroke="#0ea5e9" strokeWidth="2.5" />
                        </g>
                      ))}
                    </g>
                  );
                })()}

              </svg>
            </div>

            <div className="flex flex-wrap items-center justify-between text-[11px] text-white/50 pt-1">
              <span>X-Axis: Octave Frequency (Hz)</span>
              <span>Y-Axis: Hearing Threshold (dB HL)</span>
              <span className="text-[#ffd64e]">Shaded: Human Conversational Speech</span>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};
